'use client';

import React from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useGetFreshFacesLocationsQuery } from 'src/api/redux';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import FreshFacesLocationRow from './FreshFacesLocationRow';

const CompactFreshFacesLocations: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId: entityId } = getEntityIdentifiers(entityDetails);
  const strategy = widgetConfig?.widget?.fresh_faces_strategy;
  const strategyId = strategy?.id;
  const lookBackMonths = widgetConfig?.widget?.look_back_months;

  const { data, isLoading } = useGetFreshFacesLocationsQuery(
    {
      entityId: entityId!,
      strategyId: strategyId!,
      lookBackMonths: lookBackMonths!,
    },
    { skip: !entityId || !strategyId || lookBackMonths == null }
  );

  const locations = data ?? [];

  return (
    <Card
      className="p-4 flex flex-col gap-4 bg-white h-full"
      data-testid="compact-fresh-faces-locations"
    >
      <WidgetHeader fallbackTitle="Fresh Faces Locations" />

      <div className="flex-1 min-h-0 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl">
        {isLoading ? (
          <WidgetLoading data-testid="compact-fresh-faces-locations-loading" />
        ) : locations.length === 0 ? (
          <WidgetEmpty data-testid="compact-fresh-faces-locations-empty" variant="full" message="No new billing locations found" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="Fresh Faces Locations"
            data-testid="compact-fresh-faces-locations-list"
          >
            <div className="flex items-center justify-between pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10" data-testid="compact-fresh-faces-locations-header" role="row" aria-label="Column headers">
              <span className="text-xs font-semibold text-tertiary" role="columnheader">
                Location
              </span>
              <div className="flex gap-4">
                <span className="text-xs font-semibold text-tertiary min-w-14 text-end" role="columnheader">
                  Claims
                </span>
                <span className="text-xs font-semibold text-tertiary min-w-20 text-end" role="columnheader">
                  First Billed
                </span>
              </div>
            </div>
            {locations.map((location, index) => (
              <FreshFacesLocationRow
                key={`${location.provider_id}-${index}`}
                location={location}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactFreshFacesLocations;
